import {
  createElement,
  forwardRef,
  memo,
  HTMLAttributes,
  AnchorHTMLAttributes,
  AriaRole,
  useRef,
  useCallback,
} from 'react'
import { styled, Style } from 'inlines'
import { TEXT_VARIANT_STYLES, Typography } from '../utils/typography'
import { mergeRefs } from '../utils/mergeRefs'
import { ThemeColors, useTheme } from '../providers/useTheme'

type BaseTextHTMLProps = Omit<
  HTMLAttributes<HTMLElement> & AnchorHTMLAttributes<HTMLAnchorElement>,
  'color' | 'style' | 'children'
>

export type TextProps = BaseTextHTMLProps & {
  as?: keyof JSX.IntrinsicElements
  children: string | HTMLElement | React.ReactElement | any
  variant?: Typography
  color?: string | 'auto' | 'gradient' | ThemeColors
  align?: Style['textAlign']
  maxWidth?: number
  isMultiline?: boolean
  isNotSelectable?: boolean
  isAlwaysVisible?: boolean
  isGradient?: boolean
  style?: Style
  'aria-label'?: string
  role?: AriaRole
}

const Text = forwardRef<HTMLElement, TextProps>(
  (
    {
      as = 'p',
      children,
      color = 'auto',
      variant = 'display-regular',
      align = 'left',
      maxWidth,
      isMultiline = false,
      isNotSelectable = false,
      isAlwaysVisible = false,
      isGradient = false,
      style,
      'aria-label': ariaLabelProp,
      ...rest
    },
    externalRef,
  ) => {
    const { colors, textGradient } = useTheme()
    const innerRef = useRef<HTMLElement | null>(null)

    const textColor: string =
      color === 'auto'
        ? colors.textSecondary
        : color === 'gradient'
          ? colors.textPrimary
          : (color as string)

    const handleRef = useCallback(
      (node: HTMLElement | null) => {
        mergeRefs(node, innerRef, externalRef)
      },
      [externalRef],
    )

    const whiteSpace: Style['whiteSpace'] = isMultiline
      ? 'pre-line'
      : isAlwaysVisible
        ? 'normal'
        : 'nowrap'

    const overflow: Style['overflow'] =
      isMultiline || isAlwaysVisible ? 'visible' : 'hidden'

    const textOverflow: Style['textOverflow'] =
      isMultiline || isAlwaysVisible ? 'clip' : 'ellipsis'

    const baseStyle: Style = {
      position: 'relative',
      textAlign: align,
      fontVariantNumeric: 'tabular-nums',
      lineHeight: 'normal',
      overflowWrap: 'break-word',
      textRendering: 'optimizeLegibility',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      WebkitTextSizeAdjust: '100%',
      MozTextSizeAdjust: '100%',
      textSizeAdjust: '100%',
      textDecoration: 'none',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
      maxWidth,
      whiteSpace,
      overflow,
      textOverflow,
      // cursor: 'default',
      ...TEXT_VARIANT_STYLES[variant],
      ...style,
    }

    const gradientStyle: Style | undefined =
      textGradient && variant === 'title'
        ? {
            background: `linear-gradient(90deg, ${colors.primaryAccent} 0%, ${colors.secondaryAccent} 100%)`,
            color: textColor,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }
        : variant === 'title'
          ? {
              color: colors.primaryAccent,
            }
          : {
              color: textColor,
            }

    return createElement(styled[as] as any, {
      ...rest,
      ...(ariaLabelProp ? { 'aria-label': ariaLabelProp } : null),
      style: {
        ...gradientStyle,
        ...baseStyle,
      },
      children,
      ref: handleRef,
    })
  },
)

const MemorizedText = memo(Text)

Text.displayName = 'Text'

export { MemorizedText as Text }
